// components/modals/MeetingFormModal.js

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import {
  FiTrash2,
  FiLoader,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiClock,
  FiEye,
  FiEdit,
  FiPlus,
  FiRefreshCw,
  FiPlay,
  FiCalendar,
} from 'react-icons/fi';

import Modal from '../Modal';
import MeetingEventDetailModal from './MeetingEventDetailModal';
import MeetingEventFormModal from './MeetingEventFormModal';
import AutoGrowTextarea from '../AutoGrowTextarea';
import * as meetingsApi from '../../services/meetingsApi';

// --- Compact & Dark Select Styles ---
const selectStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: '#18181b', // zinc-900
    borderColor: '#3f3f46', // zinc-700
    boxShadow: 'none',
    '&:hover': { borderColor: '#52525b' }, // zinc-600
    minHeight: '36px',
    borderRadius: '0.375rem',
    fontSize: '0.75rem',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#18181b',
    borderColor: '#3f3f46',
    borderWidth: '1px',
    borderRadius: '0.375rem',
    zIndex: 60,
    padding: '0.25rem',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? '#2563eb' : isFocused ? '#27272a' : '#18181b',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0.375rem 0.5rem',
    borderRadius: '0.25rem',
  }),
  singleValue: (styles) => ({ ...styles, color: '#e4e4e7' }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: '#27272a',
    borderRadius: '0.25rem',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: '#e4e4e7',
    fontSize: '0.75rem',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: '#94a3b8',
    ':hover': { backgroundColor: '#ef4444', color: 'white' },
  }),
  input: (styles) => ({ ...styles, color: 'white', margin: 0, padding: 0 }),
  placeholder: (styles) => ({
    ...styles,
    color: '#71717a',
    fontSize: '0.75rem',
  }),
};

const selectStylesHigher = {
  ...selectStyles,
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#18181b',
    border: '1px solid #3f3f46',
    zIndex: 61,
    padding: '0.25rem',
    borderRadius: '0.375rem',
  }),
};

// --- Status Icon Helper ---
const EventStatusIcon = ({ status }) => {
  const iconClass = 'w-3.5 h-3.5';
  switch (status) {
    case 'processed':
      return (
        <FiCheckCircle
          className={`${iconClass} text-emerald-500`}
          title="Processed"
        />
      );
    case 'queued':
      return (
        <FiLoader
          className={`${iconClass} animate-spin text-cyan-500`}
          title="Queued"
        />
      );
    case 'processing':
      return (
        <FiLoader
          className={`${iconClass} animate-spin text-amber-500`}
          title="Processing"
        />
      );
    case 'reprocessing':
      return (
        <FiRefreshCw
          className={`${iconClass} animate-spin text-orange-400`}
          title="Reprocessing"
        />
      );
    case 'pending':
      return (
        <FiClock className={`${iconClass} text-[#5652CF]`} title="Pending" />
      );
    case 'failed':
      return (
        <FiXCircle className={`${iconClass} text-red-500`} title="Failed" />
      );
    default:
      return (
        <FiAlertCircle
          className={`${iconClass} text-zinc-500`}
          title="Unknown"
        />
      );
  }
};

const MeetingFormModal = ({
  isOpen,
  onClose,
  onSave,
  meeting,
  allUsers,
  accessToken,
}) => {
  const [formData, setFormData] = useState({
    ...meeting,
    startTime: meeting.startTime
      ? new Date(meeting.startTime).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newEventDate, setNewEventDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [isAddingEvent, setIsAddingEvent] = useState(false);

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEventFormOpen, setIsEventFormOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [processingEventId, setProcessingEventId] = useState(null);

  const userOptions = allUsers.map((u) => ({
    value: u.id,
    label: u.displayName,
    ...u,
  }));
  const organizerOptions = formData.attendees.map((a) => ({
    value: a.id,
    label: a.displayName,
    ...a,
  }));

  useEffect(() => {
    if (
      formData.organizer &&
      !formData.attendees.find((a) => a.id === formData.organizer.id)
    ) {
      setFormData((prev) => ({ ...prev, organizer: null }));
    }
  }, [formData.attendees, formData.organizer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAttendeeChange = (selectedOptions) => {
    setFormData((prev) => ({ ...prev, attendees: selectedOptions }));
  };

  const handleOrganizerChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, organizer: selectedOption }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const dataToSave = {
        ...formData,
        startTime: new Date(formData.startTime).toISOString(),
        attendees: formData.attendees.map((a) => ({
          _id: a.id,
          displayName: a.displayName,
          email: a.email,
        })),
        organizer: formData.organizer
          ? {
              _id: formData.organizer.id,
              displayName: formData.organizer.displayName,
              email: formData.organizer.email,
            }
          : null,
      };

      let savedMeeting;
      if (dataToSave.id) {
        savedMeeting = await meetingsApi.updateMeeting(
          accessToken,
          dataToSave.id,
          dataToSave,
        );
      } else {
        savedMeeting = await meetingsApi.createMeeting(accessToken, dataToSave);
      }
      onSave(savedMeeting);
      toast.success(`Meeting ${dataToSave.id ? 'updated' : 'created'}!`);
      onClose();
    } catch (err) {
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddMeetingEvent = async () => {
    if (!formData.id) {
      toast.error('Please save the new meeting before adding events.');
      return;
    }
    setIsAddingEvent(true);
    try {
      const updatedMeeting = await meetingsApi.createMeetingEvent(
        accessToken,
        formData.id,
        newEventDate,
      );
      setFormData({ ...formData, meetingEvents: updatedMeeting.meetingEvents });
      onSave(updatedMeeting);
      toast.success('Event added and queuing.');
      setNewEventDate(new Date().toISOString().slice(0, 10));
    } catch (err) {
      toast.error(`Failed to add event: ${err.message}`);
    } finally {
      setIsAddingEvent(false);
    }
  };

  const handleDeleteMeetingEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      const updatedMeeting = await meetingsApi.deleteMeetingEvent(
        accessToken,
        formData.id,
        eventId,
      );
      setFormData({ ...formData, meetingEvents: updatedMeeting.meetingEvents });
      onSave(updatedMeeting);
      toast.success('Event deleted.');
    } catch (err) {
      toast.error(`Failed to delete: ${err.message}`);
    }
  };

  const handleProcessEvent = async (eventId) => {
    setProcessingEventId(eventId);
    try {
      const updatedMeeting = await meetingsApi.processMeetingEvent(
        accessToken,
        formData.id,
        eventId,
      );
      const formattedEvents = updatedMeeting.meetingEvents.map((e) => ({
        ...e,
        id: e._id,
      }));
      setFormData({ ...formData, meetingEvents: formattedEvents });
      onSave(updatedMeeting);
      toast.success('Event queued.');
    } catch (err) {
      toast.error(`Failed to queue: ${err.message}`);
    } finally {
      setProcessingEventId(null);
    }
  };

  const handleViewEventDetails = (event) => {
    setSelectedEvent(event);
    setIsDetailModalOpen(true);
  };

  const handleEditEventTranscripts = (event) => {
    setSelectedEvent(event);
    setIsEventFormOpen(true);
  };

  const handleEventFormSave = (updatedMeeting) => {
    const formattedEvents = updatedMeeting.meetingEvents.map((e) => ({
      ...e,
      id: e._id,
    }));
    setFormData({ ...formData, meetingEvents: formattedEvents });
    onSave(updatedMeeting);
    const updatedEvent = formattedEvents.find((e) => e.id === selectedEvent.id);
    setSelectedEvent(updatedEvent);
  };

  return (
    <>
      <MeetingEventDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        meeting={formData}
        meetingEvent={selectedEvent}
        accessToken={accessToken}
      />

      <MeetingEventFormModal
        isOpen={isEventFormOpen}
        onClose={() => {
          setIsEventFormOpen(false);
          setSelectedEvent(null);
        }}
        onSave={handleEventFormSave}
        meetingId={formData.id}
        event={selectedEvent}
        accessToken={accessToken}
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={formData.id ? 'Edit Meeting' : ' Create New Meeting '}
        size="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* --- Basic Info Section --- */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="meeting-title"
                className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Meeting Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
                required
                placeholder="e.g. Daily Standup"
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Description
              </label>
              <AutoGrowTextarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full resize-none rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
                placeholder="Optional description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="start-time"
                  className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-400"
                >
                  Start Time (Local)
                </label>
                <input
                  type="datetime-local"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs text-white transition-all [color-scheme:dark] focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="duration"
                  className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-400"
                >
                  Duration (min)
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  min="1"
                  className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
                  required
                />
              </div>
            </div>
          </div>

          {/* --- Participants Section --- */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-4">
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="attendees"
                  className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-400"
                >
                  Attendees
                </label>
                <Select
                  isMulti
                  options={userOptions}
                  value={formData.attendees}
                  onChange={handleAttendeeChange}
                  styles={selectStyles}
                  placeholder="Select attendees..."
                  className="text-xs"
                />
              </div>

              <div>
                <label
                  htmlFor="organizer"
                  className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-400"
                >
                  Organizer
                </label>
                <Select
                  options={organizerOptions}
                  value={formData.organizer}
                  onChange={handleOrganizerChange}
                  styles={selectStylesHigher}
                  placeholder={
                    formData.attendees.length === 0
                      ? 'Add attendees first...'
                      : 'Select organizer...'
                  }
                  isClearable
                  isDisabled={formData.attendees.length === 0}
                  className="text-xs"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  name="isRecurring"
                  id="isRecurring"
                  checked={formData.isRecurring}
                  onChange={handleChange}
                  className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-[#5652CF] focus:ring-offset-0"
                />
                <label
                  htmlFor="isRecurring"
                  className="cursor-pointer select-none text-xs font-medium text-zinc-300"
                >
                  Recurring Meeting
                </label>
              </div>
            </div>
          </div>

          {/* --- Event Management Section (Edit Mode Only) --- */}
          {formData.id && (
            <div className="pt-2">
              <h4 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-300">
                <FiCalendar className="h-3.5 w-3.5" /> Meeting Events
              </h4>

              {/* Add Event Bar */}
              <div className="mb-3 flex items-center gap-2 rounded-md border border-zinc-700 bg-zinc-900 p-2">
                <input
                  type="date"
                  value={newEventDate}
                  onChange={(e) => setNewEventDate(e.target.value)}
                  className="flex-grow border-none bg-transparent px-2 py-1 text-xs text-white [color-scheme:dark] focus:ring-0"
                />
                <button
                  type="button"
                  onClick={handleAddMeetingEvent}
                  disabled={isAddingEvent}
                  className="flex items-center justify-center gap-1.5 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-500"
                >
                  {isAddingEvent ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <>
                      <FiPlus className="h-3 w-3" /> Add
                    </>
                  )}
                </button>
              </div>

              {/* Events List */}
              <div className="custom-scrollbar max-h-48 space-y-1.5 overflow-y-auto pr-1">
                {formData.meetingEvents.sort(
                  (a, b) => new Date(b.date) - new Date(a.date),
                ).length > 0 ? (
                  formData.meetingEvents.map((e) => {
                    const isProcessingThis = processingEventId === e.id;
                    const canProcess =
                      (e.status === 'pending' || e.status === 'failed') &&
                      e.transcripts.length > 0;

                    return (
                      <div
                        key={e.id}
                        className="group flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-800/50 p-2 transition-colors hover:border-zinc-700 hover:bg-zinc-800"
                      >
                        <div className="flex items-center gap-3">
                          <EventStatusIcon status={e.status} />
                          <div>
                            <p className="text-xs font-medium text-zinc-200">
                              {new Date(e.date).toLocaleDateString(undefined, {
                                dateStyle: 'medium',
                              })}
                            </p>
                            <p className="text-[10px] text-zinc-500">
                              {e.transcripts.length} transcript
                              {e.transcripts.length !== 1 && 's'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleProcessEvent(e.id)}
                            disabled={!canProcess || isProcessingThis}
                            title={
                              canProcess ? 'Process' : 'Add transcript first'
                            }
                            className="rounded p-1.5 text-zinc-400 transition-all hover:bg-zinc-700 hover:text-emerald-400 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            {isProcessingThis ? (
                              <FiLoader className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FiPlay className="h-3.5 w-3.5" />
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleViewEventDetails(e)}
                            disabled={e.status !== 'processed'}
                            title="View Results"
                            className="rounded p-1.5 text-zinc-400 transition-all hover:bg-zinc-700 hover:text-blue-400 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <FiEye className="h-3.5 w-3.5" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleEditEventTranscripts(e)}
                            title="Edit Transcripts"
                            className="rounded p-1.5 text-zinc-400 transition-all hover:bg-zinc-700 hover:text-white"
                          >
                            <FiEdit className="h-3.5 w-3.5" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDeleteMeetingEvent(e.id)}
                            title="Delete"
                            className="rounded p-1.5 text-zinc-400 transition-all hover:bg-zinc-700 hover:text-red-400"
                          >
                            <FiTrash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-md border border-dashed border-zinc-800 py-6 text-center">
                    <p className="text-xs text-zinc-500">No events yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* --- Footer Actions --- */}
          <div className="mt-2 flex justify-end gap-2 border-t border-zinc-800 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-24 items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-[#5652CF] disabled:bg-zinc-700 disabled:text-zinc-500"
            >
              {isSubmitting ? <FiLoader className="animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default MeetingFormModal;
